import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../../config';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ShoppingBag, Menu, X, ChevronDown, Phone, Globe, ChevronLeft, Leaf, Heart, Droplets, FlaskConical, Apple, Flower2, Zap, Shield, Star, Package, Loader2 } from 'lucide-react';
import axios_instance from "../../api/axios_instance";
import { useLanguage, LANGUAGES } from '../../i18n/LanguageContext';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const catBarRef = useRef(null);

  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  const { t, lang, setLang, dir, getLabel } = useLanguage();
  const cartItems = useSelector((state) => state.cart?.items) || [];
  const itemCount = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios_instance.get('/api/categories');
        const parents = res.data.filter(cat => !cat.parentCategory);
        const structured = parents.map(parent => ({
          ...parent,
          subCategories: res.data.filter(child => (child.parentCategory?._id || child.parentCategory) === parent._id)
        }));
        setCategories(structured);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Live search debounce — shared for desktop + mobile
  const activeQuery = searchQuery || mobileSearchQuery;
  useEffect(() => {
    const q = activeQuery.trim();
    if (q.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios_instance.get(`/api/products?q=${encodeURIComponent(q)}&limit=6`);
        const list = res.data.products ?? res.data ?? [];
        setSearchResults(list);
        setShowDropdown(true);
      } catch { setSearchResults([]); setShowDropdown(true); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [activeQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      setShowDropdown(false);
      navigate(`/products?q=${encodeURIComponent(q)}`);
      setSearchQuery('');
    }
  };

  const handleResultClick = (slug) => {
    setShowDropdown(false);
    setSearchQuery('');
    navigate(`/products/${slug}`);
  };


  const checkScroll = useCallback(() => {
    const el = catBarRef.current;
    if (!el) return;
    const scrollLeft = Math.abs(el.scrollLeft);
    setCanLeft(scrollLeft > 4);
    setCanRight(scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, [dir]);

  useEffect(() => {
    const el = catBarRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => { el.removeEventListener('scroll', checkScroll); window.removeEventListener('resize', checkScroll); };
  }, [checkScroll, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollCatBar = (direction) => {
    const el = catBarRef.current;
    if (!el) return;
    el.scrollBy({ left: (dir === 'rtl' ? -1 : 1) * direction * 200, behavior: 'smooth' });
  };

  const currentLang = LANGUAGES.find(l => l.code === lang);

  const getCategoryIcon = (name = '') => {
    const n = (name?.ar || name?.en || name || '').toLowerCase();
    if (/قلب|heart|cardiovascular|ضغط|혈압/.test(n)) return <Heart size={15} className="text-red-400" />;
    if (/مفاصل|روماتيزم|joint|arthritis|عظام|bone/.test(n)) return <Zap size={15} className="text-amber-500" />;
    if (/هضم|معدة|digestive|stomach|أمعاء|intestin|كبد|liver/.test(n)) return <Droplets size={15} className="text-blue-400" />;
    if (/مناعة|immunity|immune|دفاع/.test(n)) return <Shield size={15} className="text-emerald-500" />;
    if (/أعشاب|herb|plant|نبات|عشب/.test(n)) return <Leaf size={15} className="text-emerald-600" />;
    if (/زيت|oil|essential|عطر|parfum/.test(n)) return <FlaskConical size={15} className="text-purple-400" />;
    if (/فاكهة|fruit|تمر|date|عسل|honey|مل/.test(n)) return <Apple size={15} className="text-orange-400" />;
    if (/ورد|rose|flower|زهر|بتلة/.test(n)) return <Flower2 size={15} className="text-pink-400" />;
    if (/طاقة|energy|vitality|نشاط|تعب|fatigue/.test(n)) return <Star size={15} className="text-yellow-400" />;
    if (/تناسل|خصوبة|fertility|جنسي|sexual/.test(n)) return <Heart size={15} className="text-pink-500" />;
    if (/وزن|رجيم|diet|slim|obesity/.test(n)) return <Zap size={15} className="text-green-500" />;
    if (/جلد|بشرة|skin|شعر|hair/.test(n)) return <Flower2 size={15} className="text-rose-400" />;
    if (/سكر|diabetes|ضغط|pressure/.test(n)) return <Droplets size={15} className="text-cyan-500" />;
    if (/خلطة|mélange|blend|mix/.test(n)) return <Package size={15} className="text-violet-500" />;
    return <Leaf size={15} className="text-emerald-500" />;
  };

  return (
    <nav
      dir={dir}
      className={`w-full bg-white sticky top-0 z-[100] transition-all duration-300 ${scrolled ? 'border-b border-gray-200' : 'border-b border-gray-100'}`}
    >
      {/* ── Top bar (light) ── */}
      <div className="bg-emerald-50 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-xs">
          <a href={`tel:${t('nav.phone')}`} className="flex items-center gap-2 font-bold text-emerald-700 hover:text-emerald-900 transition-colors">
            <Phone size={12} className="text-emerald-500 shrink-0" />
            <span dir="ltr">{t('nav.phone')}</span>
          </a>

          {/* Language picker */}
          <div className="relative">
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 font-bold text-emerald-700 hover:text-emerald-900 transition-colors px-2 py-1 rounded-lg hover:bg-emerald-100"
            >
              <Globe size={12} className="text-emerald-500" />
              <span>{currentLang?.label}</span>
              <ChevronDown size={10} className={`transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLangOpen && (
              <div className="absolute top-full end-0 mt-2 w-36 bg-white text-gray-800 border border-gray-100 rounded-2xl py-1.5 z-[150] overflow-hidden">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setIsLangOpen(false); }}
                    className={`w-full text-start px-4 py-2.5 text-xs font-bold transition-colors hover:bg-emerald-50 hover:text-emerald-600 ${lang === l.code ? 'text-emerald-600 bg-emerald-50/50' : 'text-gray-700'}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main header ── */}
      <div className="max-w-7xl mx-auto px-4 py-3 md:py-4 flex items-center gap-3 md:gap-6">
        {/* Mobile hamburger */}
        <button
          className="lg:hidden w-9 h-9 flex items-center justify-center text-gray-600 hover:text-emerald-600 transition-all rounded-xl hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>

        {/* Logo */}
        <Link to="/" className="shrink-0">
          <img src="/logo.png" alt="BenMoussa" className="h-11 md:h-14 w-auto" />
        </Link>

        {/* Search – desktop */}
        <div ref={searchRef} className="hidden md:flex flex-1 relative max-w-2xl mx-auto">
          <form onSubmit={handleSearch} className="w-full relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              placeholder={t('nav.searchPlaceholder')}
              className="w-full bg-gray-50 border border-gray-200 rounded-full py-2.5 px-5 pe-12 text-sm outline-none focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-50 transition-all"
            />
            <button type="submit" className="absolute end-1 top-1/2 -translate-y-1/2 bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded-full transition-colors">
              {searchLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            </button>
          </form>

          {/* Live results dropdown */}
          {showDropdown && (
            <div className="absolute top-full mt-2 w-full bg-white border border-gray-100 rounded-2xl z-[200] overflow-hidden" dir={dir}>
              {searchLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 size={18} className="animate-spin text-emerald-600" />
                </div>
              ) : searchResults.length === 0 ? (
                <div className="px-4 py-5 text-center text-sm font-bold text-gray-400">
                  {t('nav.noResults') || 'لا توجد نتائج مطابقة'}
                </div>
              ) : (
                <>
                  {searchResults.map((p) => {
                    const firstVariant = p.variants?.[0];
                    const price = firstVariant
                      ? (firstVariant.isOnSale && firstVariant.discountPrice > 0 ? firstVariant.discountPrice : firstVariant.price)
                      : null;
                    return (
                      <button
                        key={p._id}
                        onMouseDown={() => handleResultClick(p.slug)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors border-b border-gray-50 last:border-0 text-start"
                      >
                        <div className="w-10 h-10 rounded-xl border border-gray-100 overflow-hidden shrink-0 bg-white">
                          <img src={p.images?.[0] ? `${API_URL}/${p.images[0]}` : '/placeholder.jpg'} alt="" className="w-full h-full object-contain p-1" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-800 truncate">{getLabel(p.name)}</p>
                          <p className="text-[10px] font-bold text-gray-400">{getLabel(p.category?.[0]?.name || p.category?.name)}</p>
                        </div>
                        {price != null && (
                          <span className="text-sm font-black text-emerald-600 shrink-0">{Number(price).toFixed(3)} د.ت</span>
                        )}
                      </button>
                    );
                  })}
                  <button
                    onMouseDown={handleSearch}
                    className="w-full px-4 py-2.5 text-xs font-black text-emerald-600 hover:bg-emerald-50 transition-colors text-center border-t border-gray-100"
                  >
                    {t('nav.seeAllResults') || `عرض كل النتائج لـ "${searchQuery}"`}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Mobile search icon */}
        <button
          className="md:hidden ms-auto w-9 h-9 flex items-center justify-center text-gray-600 hover:text-emerald-600 transition-all rounded-xl hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
          onClick={() => { setMobileSearchOpen(o => !o); setMobileSearchQuery(''); setSearchResults([]); }}
          aria-label="Search"
        >
          {mobileSearchOpen ? <X size={20} /> : <Search size={20} />}
        </button>

        {/* Cart */}
        <Link
          to="/cart"
          className="relative md:ms-auto w-9 h-9 flex items-center justify-center text-gray-600 hover:text-emerald-600 transition-all rounded-xl hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
          aria-label="Cart"
        >
          <ShoppingBag size={20} />
          {itemCount > 0 && (
            <span className="absolute -top-0.5 -end-0.5 bg-emerald-500 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center border-2 border-white leading-none">
              {itemCount}
            </span>
          )}
        </Link>
      </div>

      {/* ── Mobile search panel ── */}
      <AnimatePresence>
        {mobileSearchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="md:hidden border-t border-gray-100 bg-white px-4 py-3"
            dir={dir}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const q = mobileSearchQuery.trim();
                if (q) {
                  setMobileSearchOpen(false);
                  setSearchResults([]);
                  navigate(`/products?q=${encodeURIComponent(q)}`);
                  setMobileSearchQuery('');
                }
              }}
              className="relative"
            >
              <input
                ref={mobileSearchInputRef}
                autoFocus
                type="text"
                value={mobileSearchQuery}
                onChange={(e) => setMobileSearchQuery(e.target.value)}
                placeholder={t('nav.searchPlaceholder')}
                className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3 px-5 pe-12 text-sm outline-none focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-50 transition-all font-bold"
              />
              <button type="submit" className="absolute end-1.5 top-1/2 -translate-y-1/2 bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded-xl transition-colors">
                {searchLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </button>
            </form>

            {/* Live results */}
            {showDropdown && (
              <div className="mt-2 bg-white border border-gray-100 rounded-2xl overflow-hidden">
                {searchResults.length === 0 ? (
                  <div className="px-4 py-5 text-center text-sm font-bold text-gray-400">
                    {t('nav.noResults') || 'لا توجد نتائج مطابقة'}
                  </div>
                ) : (
                  <>
                    {searchResults.map((p) => {
                      const fv = p.variants?.[0];
                      const price = fv ? (fv.isOnSale && fv.discountPrice > 0 ? fv.discountPrice : fv.price) : null;
                      return (
                        <button
                          key={p._id}
                          onMouseDown={() => { setMobileSearchOpen(false); setSearchResults([]); handleResultClick(p.slug); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors border-b border-gray-50 last:border-0 text-start"
                        >
                          <div className="w-10 h-10 rounded-xl border border-gray-100 overflow-hidden shrink-0 bg-white">
                            <img src={p.images?.[0] ? `${API_URL}/${p.images[0]}` : '/placeholder.jpg'} alt="" className="w-full h-full object-contain p-1" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-800 truncate">{getLabel(p.name)}</p>
                            <p className="text-[10px] font-bold text-gray-400">{getLabel(p.category?.[0]?.name || p.category?.name)}</p>
                          </div>
                          {price != null && (
                            <span className="text-sm font-black text-emerald-600 shrink-0">{Number(price).toFixed(3)} د.ت</span>
                          )}
                        </button>
                      );
                    })}
                    <button
                      onMouseDown={() => {
                        const q = mobileSearchQuery.trim();
                        if (q) { setMobileSearchOpen(false); setSearchResults([]); navigate(`/products?q=${encodeURIComponent(q)}`); setMobileSearchQuery(''); }
                      }}
                      className="w-full px-4 py-2.5 text-xs font-black text-emerald-600 hover:bg-emerald-50 transition-colors text-center border-t border-gray-100"
                    >
                      {t('nav.seeAllResults')}
                    </button>
                  </>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Desktop category bar ── */}
      <div className="hidden lg:block border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-0 relative">

          {/* Left arrow */}
          <motion.button
            onClick={() => scrollCatBar(-1)}
            animate={{ opacity: canLeft ? 0.8 : 0, pointerEvents: canLeft ? 'auto' : 'none' }}
            whileHover={{ opacity: 1, scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            transition={{ duration: 0.18 }}
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-emerald-600 text-white z-10"
          >
            <ChevronLeft size={14} />
          </motion.button>

          {/* Scrollable track */}
          <div
            ref={catBarRef}
            className="flex items-center gap-0 overflow-x-auto flex-1 no-scrollbar"
          >
            {categories.map((cat) => (
              <div
                key={cat._id}
                className="relative shrink-0"
                onMouseEnter={() => setActiveDropdown(cat._id)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={`/products/category/${cat.slug}`}
                  className="flex items-center gap-1 px-4 py-3.5 text-[13px] font-bold text-gray-600 hover:text-emerald-600 whitespace-nowrap transition-colors border-b-2 border-transparent hover:border-emerald-600"
                >
                  {getLabel(cat.name)}
                  {cat.subCategories.length > 0 && <ChevronDown size={13} className="mt-0.5" />}
                </Link>

                {cat.subCategories.length > 0 && activeDropdown === cat._id && (
                  <div className="absolute top-full start-0 w-56 bg-white border border-gray-100 rounded-2xl py-2 z-[110]">
                    {cat.subCategories.map(sub => (
                      <Link
                        key={sub._id}
                        to={`/products/category/${sub.slug}`}
                        className="block px-5 py-2.5 text-xs font-bold text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                      >
                        {getLabel(sub.name)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right arrow */}
          <motion.button
            onClick={() => scrollCatBar(1)}
            animate={{ opacity: canRight ? 0.8 : 0, pointerEvents: canRight ? 'auto' : 'none' }}
            whileHover={{ opacity: 1, scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            transition={{ duration: 0.18 }}
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-emerald-600 text-white z-10"
          >
            <ChevronLeft size={14} className="rotate-180" />
          </motion.button>

        </div>
      </div>

      {/* ── Mobile drawer ── */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[200] lg:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          <div
            className={`absolute top-0 h-full w-[85%] max-w-sm bg-white flex flex-col ${dir === 'rtl' ? 'right-0' : 'left-0'}`}
            style={{ animation: 'slideIn 0.25s ease-out' }}
          >
            {/* Drawer header */}
            <div className="p-5 flex items-center justify-between border-b border-gray-100">
              <img src="/logo.png" alt="BenMoussa" className="h-10 w-auto" />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-xl hover:bg-gray-50"
              >
                <X size={20} />
              </button>
            </div>

            {/* Category list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {categories.map((cat) => (
                <Link
                  key={cat._id}
                  to={`/products/category/${cat.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-4 rounded-2xl border border-gray-100 text-sm font-black text-gray-900 hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors"
                >
                  <span className="w-8 h-8 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                    {getCategoryIcon(cat.name)}
                  </span>
                  {getLabel(cat.name)}
                </Link>
              ))}
            </div>

          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(${dir === 'rtl' ? '100%' : '-100%'}); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
